import React, { useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export function useBulk() {
  const { user } = useAuth();
  const [sel, setSel] = useState({});
  const toggle = useCallback((k, item) => setSel((p) => {
    const n = { ...p };
    if (n[k]) delete n[k]; else n[k] = item;
    return n;
  }), []);
  const setAll = useCallback((entries, on) => setSel((p) => {
    const n = { ...p };
    entries.forEach(([k, item]) => { if (on) n[k] = item; else delete n[k]; });
    return n;
  }), []);
  const clear = useCallback(() => setSel({}), []);
  return { enabled: user?.role === "it_admin", sel, toggle, setAll, clear, has: (k) => !!sel[k], count: Object.keys(sel).length };
}

export function BulkCheck({ bulk, k, item, testId }) {
  if (!bulk.enabled) return null;
  return (
    <input type="checkbox" checked={bulk.has(k)} onClick={(e) => e.stopPropagation()} onChange={() => bulk.toggle(k, item)}
      className="h-4 w-4 accent-emerald-700 cursor-pointer" data-testid={testId || `bulk-check-${k}`} />
  );
}

export function BulkCheckAll({ bulk, entries, testId = "bulk-check-all" }) {
  if (!bulk.enabled) return null;
  const all = entries.length > 0 && entries.every(([k]) => bulk.has(k));
  return (
    <input type="checkbox" checked={all} onClick={(e) => e.stopPropagation()} onChange={() => bulk.setAll(entries, !all)}
      className="h-4 w-4 accent-emerald-700 cursor-pointer" title="Pilih semua" data-testid={testId} />
  );
}

export function BulkBar({ bulk, onDone, label = "data", testId = "bulk-bar" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!bulk.enabled || bulk.count === 0) return null;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/bulk-delete", { items: Object.values(bulk.sel) });
      toast.success(`${r.data.deleted} ${label} dihapus permanen${r.data.failed ? `, ${r.data.failed} gagal` : ""}.`);
      setOpen(false); bulk.clear(); onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="sticky top-16 z-20 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 shadow-sm" data-testid={testId}>
      <span className="text-sm font-medium text-rose-800" data-testid={`${testId}-count`}>{bulk.count} {label} dipilih</span>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={bulk.clear} data-testid={`${testId}-clear`}><X className="h-4 w-4 mr-1" /> Batal Pilih</Button>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-rose-600 hover:bg-rose-700" data-testid={`${testId}-delete`}><Trash2 className="h-4 w-4 mr-1" /> Hapus Terpilih</Button>
      </div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`${testId}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {bulk.count} {label}?</AlertDialogTitle>
            <AlertDialogDescription>Semua data terpilih akan dihapus permanen beserta efeknya (saldo kas, status, piutang, laporan & rekap) dan tidak dapat dikembalikan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-confirm-cancel`}>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid={`${testId}-confirm-ok`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ya, Hapus Permanen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
